import React, { useState, useEffect, useCallback } from "react";
import "./NewPatientRegistration.css"; // Using your existing luxury styles

const ItemMaster = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [items, setItems] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [itemImages, setItemImages] = useState([]);
  // Dynamic Data from APIs
  const [mainGroups, setMainGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [filteredSubGroups, setFilteredSubGroups] = useState([]);

  const initialFormState = {
    itemCd: "",
    itemDesc: "",
    hsnCode: "",
    taxRate: "",
    closeFlag: "N",
    itc: "N",
    itemUOM: "",
    itemLocation: "",
    origin: "",
    cth: "",
    minQty: "",
    Main_Grp: "",
    Sub_Grp: "",
    grade: "",
    validity: "N",
    maxQty: "",
    abc: "",
    ved: "",
    reOrderQty: "",
    make: "",
    prodCode: "",
    blockUnblock: "",
    accountCode: "",
    image: null
  };

  const [formData, setFormData] = useState(initialFormState);

  // Load Initial Data
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const [mainRes, subRes, latestRes] = await Promise.all([
          fetch("https://hmsapi.emedhatechnologies.com/master/api/api/maingroup"),
          fetch("https://hmsapi.emedhatechnologies.com/master/api/api/subgroup"),
          fetch("https://hmsapi.emedhatechnologies.com/master/api/api/itemsmasterform")
        ]);
        
        const mainData = await mainRes.json();
        const subData = await subRes.json();
        const allItems = await latestRes.json();

        setMainGroups(mainData);
        setSubGroups(subData);

        // Logic for ItemCd (Incremental)
        if (screenAction === "add" && allItems.length > 0) {
          const lastId = Math.max(...allItems.map(i => parseInt(i.itemCd) || 0));
          setFormData(prev => ({ ...prev, itemCd: (lastId + 1).toString() }));
        }
      } catch (err) {
        console.error("Initialization Error:", err);
      }
    };
    fetchGroups();
  }, [screenAction]);

  // Filter SubGroups based on MainGroup selection
// --- UPDATED SUB GROUP FILTERING LOGIC ---
  useEffect(() => {
    if (formData.Main_Grp) {
      // 1. Filter the subGroups array based on the selected Main_Grp
      // We use .toString() to avoid issues if one is a Number and one is a String
      const filtered = subGroups.filter(
        sub => sub.Main_Grp.toString() === formData.Main_Grp.toString()
      );
      
      setFilteredSubGroups(filtered);

      /**
       * 2. SMART PERSISTENCE CHECK:
       * Check if the current Sub_Grp in formData actually exists in the new filtered list.
       * This prevents the value from being cleared when loading an existing record (Edit/Search).
       */
      const currentSubValue = formData.Sub_Grp ? formData.Sub_Grp.toString() : "";
      const isSubValid = filtered.some(s => s.Sub_Grp.toString() === currentSubValue);

      // Only reset to empty if there is a value that doesn't belong to the new group
      if (currentSubValue !== "" && !isSubValid) {
        setFormData(prev => ({ ...prev, Sub_Grp: "" }));
      }
    } else {
      setFilteredSubGroups([]);
      setFormData(prev => ({ ...prev, Sub_Grp: "" }));
    }
  }, [formData.Main_Grp, subGroups]); 
  // Note: We do NOT include formData.Sub_Grp in the dependency array to avoid loops.

  const searchItems = async (text) => {
    setSearchText(text);
    if (text.length < 1) {
      setItems([]);
      return;
    }
    // Reusing your search logic but for Items
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/itemsmasterform`);
    const data = await res.json();
    const filtered = data.filter(item => 
      item.itemCd.toString().includes(text) || 
      item.itemDesc.toLowerCase().includes(text.toLowerCase())
    );
    setItems(filtered);
    setShowDropdown(true);
  };


const handleFileChange = (e) => {
  const files = Array.from(e.target.files);

  const filesWithPreview = files.map(file => ({
    file,
    preview: URL.createObjectURL(file),
    isExisting: false
  }));

  setItemImages(prev => [...prev, ...filesWithPreview]);
};

const removeImage = (index) => {
  const updated = [...itemImages];
  updated.splice(index, 1);
  setItemImages(updated);
};

const selectItem = (item) => {
  setFormData(item);

  setSearchText(`${item.itemCd} | ${item.itemDesc}`);
  setShowDropdown(false);

  // ✅ Load existing images
  if (item.image) {
    const fileList = JSON.parse(item.image);
    const formatted = fileList.map(fileName => ({
      file: null,
      preview: fileName,
      isExisting: true,
      fileName: fileName
    }));
    setItemImages(formatted);
  } else {
    setItemImages([]);
  }

  if (screenAction === "edit") setIsDisabled(false);
  if (screenAction === "view") setIsDisabled(true);
};

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? "Y" : "N") : (files ? files[0] : value)
    }));
  };

  // const handleSubmit = async () => {
  //   const user = JSON.parse(localStorage.getItem("user"));
  //   if (!formData.itemDesc) return alert("Item Description is required");

  //   const payload = { ...formData, created_by: user?.id };
  //   delete payload.created_at; // Clean payload
    
  //   const method = formData.hospital_id ? "PUT" : "POST";
  //   const url = `https://hmsapi.emedhatechnologies.com/master/api/api/itemsmasterform${method === "PUT" ? "/" + formData.itemCd : ""}`;

  //   try {
  //     const res = await fetch(url, {
  //       method,
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(payload)
  //     });
  //     if (res.ok) alert("Item Saved Successfully!");
  //   } catch (err) {
  //     alert("Error saving item");
  //   }
  // };

  // --- ACTION HELPERS ---


const handleSubmit = async () => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (!formData.itemDesc) return alert("Item Description is required");

  const formDataObj = new FormData();
  const payload = { ...formData };

  delete payload.created_at;
  payload.created_by = user?.id;

  // Append text fields
  Object.keys(payload).forEach(key => {
    formDataObj.append(key, payload[key] || "");
  });

  // ✅ Existing images
  const existingFiles = itemImages
    .filter(f => f.isExisting)
    .map(f => f.fileName);

  formDataObj.append("existing_files", JSON.stringify(existingFiles));

  // ✅ New images
  itemImages.forEach(img => {
    if (!img.isExisting) {
      formDataObj.append("item_images", img.file);
    }
  });

  const method = formData.hospital_id ? "PUT" : "POST";

  const url =
    method === "POST"
      ? "https://hmsapi.emedhatechnologies.com/master/api/api/itemsmasterform"
      : `https://hmsapi.emedhatechnologies.com/master/api/api/itemsmasterform/${formData.itemCd}`;

  const res = await fetch(url, {
    method,
    body: formDataObj
  });

  if (res.ok) {
    alert("Item Saved Successfully!");
  }
};


  const clearForm = () => {
    // Get latest Item ID to suggest next one
    const fetchLatestId = async () => {
      const res = await fetch("https://hmsapi.emedhatechnologies.com/master/api/api/itemsmasterform");
      const allItems = await res.json();
      const lastId = Math.max(...allItems.map(i => parseInt(i.itemCd) || 0), 0);
      
      setFormData({
        ...initialFormState,
        itemCd: (lastId + 1).toString()
      });
    };
    
    fetchLatestId();
    setSearchText("");
    setIsDisabled(false);
    console.log("Item Form Cleared");
  };

  const enableEditing = () => {
    setIsDisabled(false);
  };

  const disableEditing = () => {
    setIsDisabled(true);
  };

  const loadItemById = async (id) => {
    try {
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/itemsmasterform`);
      const data = await res.json();
      const item = data.find(it => it.itemCd.toString() === id.toString());
      if (item) {
        setFormData(item);
        setIsDisabled(false);
      }
    } catch (err) {
      console.error("Error loading item:", err);
    }
  };

  const refreshForm = () => {
    if (formData.itemCd && (formData.hospital_id || formData.created_at)) {
      loadItemById(formData.itemCd);
    } else {
      clearForm();
    }
  };

const printForm = () => {
  const newWindow = window.open("", "", "width=900,height=700");
  const today = new Date().toLocaleDateString();

  const htmlContent = `
    <html>
      <head>
        <title>Item Master - ${formData.itemDesc}</title>
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #333;
            margin: 0;
            padding: 40px;
          }

          .header {
            display: flex;
            justify-content: space-between;
            border-bottom: 2px solid #8e44ad;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }

          .hospital-info h1 {
            margin: 0;
            font-size: 24px;
            color: #2c3e50;
          }

          .hospital-info p {
            margin: 2px 0;
            font-size: 12px;
            color: #7f8c8d;
          }

          .report-title {
            text-align: right;
          }

          .report-title h2 {
            margin: 0;
            color: #8e44ad;
          }

          .section-header {
            background: #f5eef8;
            padding: 8px 12px;
            font-weight: bold;
            border-left: 4px solid #8e44ad;
            margin-top: 20px;
            margin-bottom: 10px;
            text-transform: uppercase;
          }

          .grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .item {
            display: flex;
            border-bottom: 1px dotted #ccc;
            padding: 6px 0;
            font-size: 13px;
          }

          .label {
            width: 160px;
            font-weight: 600;
            color: #555;
          }

          .value {
            flex: 1;
            font-weight: 500;
          }

          .full-width {
            grid-column: span 2;
          }

          /* IMAGE GALLERY */
          .image-gallery {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 10px;
          }

          .image-box {
            border: 1px solid #ddd;
            padding: 5px;
            border-radius: 5px;
          }

          .image-box img {
            width: 120px;
            height: 100px;
            object-fit: cover;
            border-radius: 3px;
          }

          .footer {
            margin-top: 50px;
            font-size: 10px;
            text-align: center;
            color: #95a5a6;
            border-top: 1px solid #eee;
            padding-top: 10px;
          }

          .sig-box {
            margin-top: 60px;
            display: flex;
            justify-content: space-between;
          }

          .sig {
            border-top: 1px solid #000;
            width: 200px;
            text-align: center;
            padding-top: 5px;
            font-size: 12px;
          }

          @media print {
            body { padding: 20px; }
            @page { margin: 0.5cm; }
          }
        </style>
      </head>

      <body>

        <div class="header">
          <div class="hospital-info">
            <h1>EMEDHA CARE HOSPITAL</h1>
            <p>Inventory & Stores Department</p>
            <p>Phone: +91 98765 43210</p>
          </div>

          <div class="report-title">
            <h2>ITEM MASTER REPORT</h2>
            <p>Date: ${today}</p>
          </div>
        </div>

        <!-- ITEM IDENTIFICATION -->
        <div class="section-header">Item Identification</div>
        <div class="grid">
          <div class="item"><span class="label">Item Code:</span><span class="value">${formData.itemCd}</span></div>
          <div class="item"><span class="label">Description:</span><span class="value">${formData.itemDesc}</span></div>
          <div class="item"><span class="label">HSN Code:</span><span class="value">${formData.hsnCode}</span></div>
          <div class="item"><span class="label">Tax Rate:</span><span class="value">${formData.taxRate}%</span></div>
          <div class="item"><span class="label">UOM:</span><span class="value">${formData.itemUOM}</span></div>
          <div class="item"><span class="label">Origin:</span><span class="value">${formData.origin}</span></div>
          <div class="item"><span class="label">Location:</span><span class="value">${formData.itemLocation}</span></div>
          <div class="item"><span class="label">Make:</span><span class="value">${formData.make}</span></div>
        </div>

        <!-- CLASSIFICATION -->
        <div class="section-header">Classification</div>
        <div class="grid">
          <div class="item"><span class="label">Main Group:</span><span class="value">${formData.Main_Grp}</span></div>
          <div class="item"><span class="label">Sub Group:</span><span class="value">${formData.Sub_Grp}</span></div>
          <div class="item"><span class="label">ABC:</span><span class="value">${formData.abc}</span></div>
          <div class="item"><span class="label">VED:</span><span class="value">${formData.ved}</span></div>
          <div class="item"><span class="label">Grade:</span><span class="value">${formData.grade}</span></div>
        </div>

        <!-- STOCK DETAILS -->
        <div class="section-header">Inventory Details</div>
        <div class="grid">
          <div class="item"><span class="label">Min Qty:</span><span class="value">${formData.minQty}</span></div>
          <div class="item"><span class="label">Max Qty:</span><span class="value">${formData.maxQty}</span></div>
          <div class="item"><span class="label">Reorder Qty:</span><span class="value">${formData.reOrderQty}</span></div>
          <div class="item"><span class="label">Account Code:</span><span class="value">${formData.accountCode}</span></div>
        </div>

        <!-- IMAGES -->
        <div class="section-header">Item Images</div>
        <div class="image-gallery">
          ${
            itemImages.length > 0
              ? itemImages.map(img => `
                <div class="image-box">
                  <img src="${img.preview}" />
                </div>
              `).join("")
              : '<p style="font-size:12px;color:#777;">No images available</p>'
          }
        </div>

        <div class="sig-box">
          <div class="sig">Store Incharge</div>
          <div class="sig">Authorized Signatory</div>
        </div>

        <div class="footer">
          This is a system-generated item master report for internal inventory tracking.
        </div>

      </body>
    </html>
  `;

  newWindow.document.write(htmlContent);
  newWindow.document.close();

  setTimeout(() => {
    newWindow.print();
    newWindow.close();
  }, 700);
};


  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  
  useEffect(() => {
  
    if(screenAction === "add"){
      clearForm();
    }
  
    if(screenAction === "edit"){
      enableEditing();
    }
  
    if(screenAction === "view"){
      disableEditing();
    }
  
    // if(screenAction === "save"){
    //   handleSubmit();
    // }
  
    if(screenAction === "refresh"){
      refreshForm();
    }
  
    if(screenAction === "print"){
      printForm();
    }
  
  }, [screenAction]);



  return (
    <div className="registration-container fade-in">
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Item Master</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search by Item Code or Description"
            onChange={(e) => searchItems(e.target.value)}
          />
          {showDropdown && items.length > 0 && (
            <div className="patient-dropdown">
              {items.map((it) => (
                <div key={it.itemCd} className="patient-item" onClick={() => selectItem(it)}>
                  {it.itemCd} | {it.itemDesc}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        {/* TOP LEVEL IDENTIFICATION */}
        <div className="form-section-header">
          <h3>Item Identification</h3>
        </div>
        <div className="form-grid">
          <div className="input-box read-only">
            <label>Item Code</label>
            <input type="text" name="itemCd" value={formData.itemCd} readOnly />
          </div>
          <div className="input-box full-width">
            <label>Item Description</label>
            <input type="text" name="itemDesc" value={formData.itemDesc} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>HSN Code</label>
            <input type="text" name="hsnCode" value={formData.hsnCode} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>Tax Rate (%)</label>
            <input type="number" name="taxRate" value={formData.taxRate} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box" style={{flexDirection: 'row', alignItems: 'center', gap: '10px', marginTop: '20px'}}>
            <input type="checkbox" name="closeFlag" checked={formData.closeFlag === "Y"} onChange={handleChange} disabled={isDisabled} />
            <label>Close Flag</label>
          </div>
          <div className="input-box">
            <label>ITC Applicable</label>
            <select name="itc" value={formData.itc} onChange={handleChange} disabled={isDisabled}>
              <option value="N">No</option>
              <option value="Y">Yes</option>
            </select>
          </div>
        </div>

        {/* CHARACTERISTICS */}
        <div className="form-section-header">
          <h3>Item Characteristics</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>ITEM UOM</label>
            <select name="itemUOM" value={formData.itemUOM} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select</option>
              <option>KG</option><option>TON</option><option>LTRS</option><option>PCS</option><option>NOS</option>
            </select>
          </div>
          <div className="input-box">
            <label>Origin</label>
            <select name="origin" value={formData.origin} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select</option>
              <option>Local</option><option>International</option>
            </select>
          </div>
          <div className="input-box">
            <label>Location</label>
            <input type="text" name="itemLocation" value={formData.itemLocation} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>CTH</label>
            <input type="text" name="cth" value={formData.cth} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>Make</label>
            <input type="text" name="make" value={formData.make} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>Prod Code</label>
            <input type="text" name="prodCode" value={formData.prodCode} onChange={handleChange} disabled={isDisabled} />
          </div>
        </div>

        {/* CLASSIFICATION */}
        <div className="form-section-header">
          <h3>Grouping & Classification</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>Main Group</label>
            <select name="Main_Grp" value={formData.Main_Grp} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select Main Group</option>
           {Array.isArray(mainGroups) && mainGroups.map(g => (
  <option key={g.Main_Grp} value={g.Main_Grp}>
    {g.Main_Grp_Desc}
  </option>
))}
            </select>
          </div>
          <div className="input-box">
           <div className="input-box">
  <label>Sub Group</label>
  <select 
    name="Sub_Grp" 
    value={formData.Sub_Grp || ""} 
    onChange={handleChange} 
    disabled={isDisabled || !formData.Main_Grp} // Keep disabled until Main Group is picked
  >
    <option value="">Select Sub Group</option>
    {filteredSubGroups.map(s => (
      <option key={s.Sub_Grp} value={s.Sub_Grp}>
        {s.Sub_Grp_Desc}
      </option>
    ))}
  </select>
</div>
          </div>
          <div className="input-box">
            <label>Grade</label>
            <select name="grade" value={formData.grade} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select</option>
              <option>A</option><option>B</option><option>C</option><option>D</option>
            </select>
          </div>
          <div className="input-box">
            <label>Validity Check</label>
            <select name="validity" value={formData.validity} onChange={handleChange} disabled={isDisabled}>
              <option value="N">No</option><option value="Y">Yes</option>
            </select>
          </div>
          <div className="input-box">
            <label>ABC Analysis</label>
            <select name="abc" value={formData.abc} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select</option>
              <option>A</option><option>B</option><option>C</option>
            </select>
          </div>
          <div className="input-box">
            <label>VED Analysis</label>
            <select name="ved" value={formData.ved} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select</option>
              <option>V</option><option>E</option><option>D</option>
            </select>
          </div>
        </div>

        {/* INVENTORY & ACCOUNTING */}
        <div className="form-section-header">
          <h3>Inventory & Accounting</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>Min Qty</label>
            <input type="number" name="minQty" value={formData.minQty} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>Max Qty</label>
            <input type="number" name="maxQty" value={formData.maxQty} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>Re-Order Qty</label>
            <input type="number" name="reOrderQty" value={formData.reOrderQty} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>Account Code</label>
            <input type="text" name="accountCode" value={formData.accountCode} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>Block / Unblock</label>
            <input type="text" name="blockUnblock" value={formData.blockUnblock} onChange={handleChange} disabled={isDisabled} />
          </div>
          <div className="input-box">
            <label>Item Image</label>
          <input
  type="file"
  multiple
  onChange={handleFileChange}
  disabled={isDisabled}
/>


          </div>
          <div className="preview-container">
  {itemImages.map((img, index) => (
    <div key={index} className="preview-box">
      <img src={img.preview} alt="item" style={{ width: 100, height: 100 }} />
      {!isDisabled && (
        <button type="button" onClick={() => removeImage(index)}>✕</button>
      )}
    </div>
  ))}
</div>
        </div>
      </form>
    </div>
  );
};

export default ItemMaster;